import { Button, Card, cn } from '@playerbase/ui';
import type { DiscordGuildChannel } from '@playerbase/types';

import { ActivityItem } from './activity-item.js';
import { BuildMarkerActivityItem } from './build-marker-activity-item.js';
import { FormSubmissionActivityItem } from './form-submission-activity-item.js';
import type {
  ActivityEntry,
  InboxMessage,
} from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Activity panel.
//
// Right-hand companion to the chronological feed. Each entry is the
// activity-driving message for a group (a thread reply, a reacted-to
// chat message), a poll-vote, or a form submission - with a small
// source label. Clicking the entry scrolls the matching pane to the
// triggering row, expands or switches tabs as needed, and pulses a
// ring on the row that drove the activity.
// ---------------------------------------------------------------------------

interface ActivityPanelProps {
  entries: ActivityEntry[];
  byExternalId: Map<string, InboxMessage>;
  // Channel index used by the form-submission activity item to render a
  // friendly `#name` source label without re-fetching guild channels.
  channelById: Map<string, DiscordGuildChannel>;
  onSelect: (entry: ActivityEntry) => void;
  visibleCount: number;
  onShowMore: () => void;
  // Per-entry unread predicate. The activity panel used to read the
  // watermark directly via a timestamp-only predicate, but that made
  // a single click clear every older entry below it (anything with
  // `lastActivityAt <= clickedTs`). The parent now composes the
  // watermark check with a local per-entry dismissal so clicking one
  // item only clears its own dot.
  isUnread: (entry: ActivityEntry) => boolean;
  // Override for the Card's max-h. The default keeps the original
  // sidebar treatment; a full-width strip layout passes a tighter
  // class so the strip doesn't eat the page.
  maxHeightClass?: string;
}

function entryKey(entry: ActivityEntry): string {
  if (entry.kind === 'group') return `group:${entry.group.id}`;
  if (entry.kind === 'form_submission') return `submission:${entry.submission.id}`;
  return `build:${entry.marker.id}`;
}

export function ActivityPanel({
  entries,
  byExternalId,
  channelById,
  onSelect,
  visibleCount,
  onShowMore,
  isUnread,
  maxHeightClass = 'max-h-[calc(100vh-7rem)]',
}: ActivityPanelProps): JSX.Element {
  const visible = entries.slice(0, visibleCount);
  const hasMore = visibleCount < entries.length;
  return (
    <Card className={cn('flex flex-col bg-surface', maxHeightClass)}>
      <Card.Header className="rounded-t-md border-b border-border bg-surface pb-4">
        <h2 className="text-sm font-medium text-text">Activity</h2>
        <p className="text-xs text-text-muted">
          Recent reactions, replies, polls and form submissions
        </p>
      </Card.Header>
      <Card.Content className="no-scrollbar flex-1 overflow-y-auto p-0">
        {entries.length === 0 ? (
          <p className="px-4 py-6 text-text-muted text-sm">No recent activity.</p>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {visible.map((entry) => {
                const unread = isUnread(entry);
                if (entry.kind === 'group') {
                  return (
                    <li key={entryKey(entry)}>
                      <ActivityItem
                        group={entry.group}
                        byExternalId={byExternalId}
                        onSelect={() => onSelect(entry)}
                        unread={unread}
                      />
                    </li>
                  );
                }
                if (entry.kind === 'build_marker') {
                  return (
                    <li key={entryKey(entry)}>
                      <BuildMarkerActivityItem
                        marker={entry.marker}
                        changedAt={entry.changedAt}
                        onSelect={() => onSelect(entry)}
                        unread={unread}
                      />
                    </li>
                  );
                }
                const channel =
                  entry.submission.surveyChannelId === null
                    ? null
                    : channelById.get(entry.submission.surveyChannelId) ?? null;
                return (
                  <li key={entryKey(entry)}>
                    <FormSubmissionActivityItem
                      entry={entry.submission}
                      channelLabel={channel?.name ?? null}
                      onSelect={() => onSelect(entry)}
                      unread={unread}
                    />
                  </li>
                );
              })}
            </ul>
            {hasMore && (
              <div className="flex justify-center border-t border-border px-4 py-3">
                <Button variant="ghost" size="sm" onClick={onShowMore}>
                  Show more
                </Button>
              </div>
            )}
          </>
        )}
      </Card.Content>
    </Card>
  );
}
